import { Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { Contact } from '../../../types/Contact';
import { UserSettingsCollapseKeys } from '../../../types/User';
import AppLink from '../../common/AppLink/AppLink';
import AddContactModal from './AddContactModal';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import { Add, Delete } from '@mui/icons-material';
import AccordionSection from '../../AccordionSection';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { RolePillsCell } from '../../preferences/RolePillsCell';

interface IContactTableProps {
  accountUuid: string;
  contacts: Contact[];
  setContacts: (contacts: Contact[]) => void;
}

export default function ContactTable({ accountUuid, contacts, setContacts }: IContactTableProps) {
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { disabled } = useContext(DisableContext);

  const onAdd = (addedContacts: Contact[]) => {
    const netNewContacts = _.differenceBy(addedContacts, contacts, 'uuid');
    setContacts([...contacts, ...netNewContacts]);
  };

  const handleRemove = () => {
    setContacts(_.reject(contacts, (contact) => _.includes(selectedContacts, contact.uuid)));
    setSelectedContacts([]);
  };

  const { data: preferenceData } = useGetAllPreferences({
    params: {
      filters: { keys: [PREFERENCES_VALID_KEYS.CONTACT_ROLE] },
    },
  });

  const prefByUuid = useMemo(() => _.keyBy(preferenceData ?? [], 'uuid'), [preferenceData]);

  const contactTableRows = contacts.map((contact: Contact) => {
    const handleSelectionChange = (checked: boolean) => {
      const newSelections = checked ? [...selectedContacts, contact.uuid] : _.reject(selectedContacts, (selectedUuid: string) => selectedUuid === contact.uuid);
      setSelectedContacts(newSelections);
    };

    return (
      <TableRow key={contact.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell>
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={_.includes(selectedContacts, contact.uuid)}
            onChange={(event) => handleSelectionChange(event.target.checked)}
          />
        </TableCell>
        <TableCell>
          <AppLink to={`/${ROUTE_PATHS.CONTACTS}/${contact.uuid}`}>{contact.full_name}</AppLink>
        </TableCell>
        <TableCell>
          <RolePillsCell rolesUuids={contact.roles_uuids} prefByUuid={prefByUuid} />
        </TableCell>
        <TableCell>{contact.email}</TableCell>
        <TableCell>{contact.phone_1}</TableCell>
      </TableRow>
    );
  });

  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'end' }}>
      <ResponsiveButton startIcon={<Delete />} variant="outlined" color="warning" onClick={handleRemove} disabled={disabled}>
        Remove
      </ResponsiveButton>
      <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)} disabled={disabled}>
        Add
      </ResponsiveButton>
    </Box>
  );

  return (
    <AccordionSection title="Contacts" headerElements={actionButtons} collapseId={UserSettingsCollapseKeys.EVENT_ITEM_CONTACTS}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{contactTableRows}</TableBody>
        </Table>
      </TableContainer>

      <AddContactModal
        accountUuid={accountUuid}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        alreadyAddedContacts={_.map(contacts, 'uuid')}
        onAdd={onAdd}
      />
    </AccordionSection>
  );
}
